/**
 * Projects feature reducer.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
    fetchProjects,
    fetchMyProjects,
    fetchProject,
    createProject,
    renameProject,
    deleteProject,
} from '../actions/projectsThunks';
import {
    fetchApsConnection,
    startApsConnect,
    disconnectAps,
    fetchApsHubs,
    fetchApsHubProjects,
    linkAccProject,
    unlinkAccProject,
    fetchTopFolders,
    fetchFolderContents,
    fetchRvtModels,
    fetchModelGroups,
    createModelGroup,
    replaceModelGroup,
    deleteModelGroup,
    type ApsConnectionResult,
} from '../actions/apsThunks';
import { ApiErrorPayload } from '../facades/apiFacade';
import { APS_CONNECTION_REVOKED } from '../facades/apsApi';
import { ApsConnection, ApsHub, ApsProject, ApsFolder, ApsFolderEntry, RvtModelNode, RvtModelEdge, ModelGroup, Project } from '../../@types/projects';

interface AsyncSlice<T> {
    data: T;
    isLoading: boolean;
    /**
     * The whole error payload rather than a message string, so components can
     * branch on `status`: a 403 from the administrative list means "show the
     * member view", not "something broke".
     */
    error: ApiErrorPayload | null;
}

/**
 * `notConnected` is the 404 from `GET /aps/connection`, which is an answer and
 * not a failure. Whether a live connection needs re-authorizing is a different
 * question, answered by `connection.status`.
 */
export type ApsLoadStatus = 'idle' | 'loading' | 'loaded' | 'notConnected' | 'error';

export type FolderNodeStatus = 'idle' | 'loading' | 'loaded' | 'error';

/**
 * One folder in the lazily-discovered tree. `subfolders` is `null` until the
 * folder has actually been expanded — an empty array means "expanded, no
 * child folders", which is a different fact worth keeping separate from
 * "never looked". Only ids are carried here; each child's own name lives on
 * its own entry, filled in the moment it is discovered as someone else's
 * child, so a folder's name is known before it is ever expanded itself.
 *
 * `models` is this folder's *direct* RVT children, from the same
 * `?type=folder,rvt` walk that discovers `subfolders` — the folder browser
 * is the source of truth for what's in a folder, not the separate
 * `/aps/rvt-models` catalog, which exists only to supply the link graph.
 */
export interface FolderNodeState {
    name: string | null;
    status: FolderNodeStatus;
    subfolders: string[] | null;
    models: ApsFolderEntry[] | null;
    error: ApiErrorPayload | null;
}

export interface FoldersState {
    top: AsyncSlice<ApsFolder[]>;
    byUrn: Record<string, FolderNodeState>;
}

export interface RvtGraphState {
    status: 'idle' | 'loading' | 'loaded' | 'error';
    computedAt: string | null;
    nodes: RvtModelNode[];
    edges: RvtModelEdge[];
    error: ApiErrorPayload | null;
}

export interface ModelGroupsState {
    status: 'idle' | 'loading' | 'loaded' | 'error';
    data: ModelGroup[];
    error: ApiErrorPayload | null;
    /** Create, edit and delete share one slot: the panel edits one group at a time. */
    mutation: { isSaving: boolean; error: ApiErrorPayload | null };
}

export interface ApsProjectState {
    status: ApsLoadStatus;
    connection: ApsConnection | null;
    error: ApiErrorPayload | null;
    hubs: AsyncSlice<ApsHub[]>;
    /** ACC projects for `hubId`. Replaced wholesale when the hub changes. */
    accProjects: AsyncSlice<ApsProject[]> & { hubId: string | null };
    /** Connect, link, unlink and disconnect share one slot: never concurrent. */
    action: { isBusy: boolean; error: ApiErrorPayload | null };
    /** Every RVT in the linked ACC project, plus its link graph. Fetched once, refreshed only on request. */
    rvtGraph: RvtGraphState;
    /** The admin's model groups for the linked ACC project. */
    modelGroups: ModelGroupsState;
    /** The ACC folder tree, discovered lazily as the picker is browsed. */
    folders: FoldersState;
}

export interface ProjectsState {
    list: AsyncSlice<Project[]>;
    current: AsyncSlice<Project | null>;
    /**
     * Create, rename and delete share one slot. They are never in flight at the
     * same time (create lives on the list page, rename and delete on the detail
     * page), so separate slots would be three copies of the same two fields.
     */
    mutation: { isSaving: boolean; error: ApiErrorPayload | null };
    /**
     * Keyed by project object_id. The projects list reads these entries to fill
     * its Autodesk column, so they outlive the detail page that created them.
     */
    aps: Record<string, ApsProjectState>;
}

const emptyRvtGraph: RvtGraphState = {
    status: 'idle',
    computedAt: null,
    nodes: [],
    edges: [],
    error: null,
};

const emptyModelGroups: ModelGroupsState = {
    status: 'idle',
    data: [],
    error: null,
    mutation: { isSaving: false, error: null },
};

const emptyFolders: FoldersState = {
    top: { data: [], isLoading: false, error: null },
    byUrn: {},
};

const emptyApsState: ApsProjectState = {
    status: 'idle',
    connection: null,
    error: null,
    hubs: { data: [], isLoading: false, error: null },
    accProjects: { hubId: null, data: [], isLoading: false, error: null },
    action: { isBusy: false, error: null },
    rvtGraph: emptyRvtGraph,
    modelGroups: emptyModelGroups,
    folders: emptyFolders,
};

/**
 * The catalog, model groups and folder tree all describe *this* ACC project.
 * A (re)link or an unlink points the connection somewhere else — or nowhere
 * — so anything cached from before belongs to a project that may no longer
 * be the one in view. Called from both `linkAccProject.fulfilled` and
 * `unlinkAccProject.fulfilled`; a first-time link has nothing to discard, so
 * this is a no-op there.
 */
function resetProjectCaches(entry: ApsProjectState): void {
    entry.rvtGraph = JSON.parse(JSON.stringify(emptyRvtGraph));
    entry.modelGroups = JSON.parse(JSON.stringify(emptyModelGroups));
    entry.folders = JSON.parse(JSON.stringify(emptyFolders));
}

const initialState: ProjectsState = {
    list: { data: [], isLoading: false, error: null },
    current: { data: null, isLoading: false, error: null },
    mutation: { isSaving: false, error: null },
    aps: {},
};

function byName(a: Project, b: Project): number {
    return a.name.localeCompare(b.name);
}

function upsertIntoList(list: Project[], project: Project): Project[] {
    const index = list.findIndex(p => p.object_id === project.object_id);
    if (index === -1) return [...list, project].sort(byName);
    const next = [...list];
    next[index] = project;
    return next.sort(byName);
}

/** Get or create the APS entry for a project, inside an immer draft. */
function apsEntry(state: ProjectsState, projectId: string): ApsProjectState {
    if (!state.aps[projectId]) {
        state.aps[projectId] = JSON.parse(JSON.stringify(emptyApsState));
    }
    return state.aps[projectId];
}

export const projectsSlice = createSlice({
    name: 'projects',
    initialState,
    reducers: {
        /**
         * Drop the loaded project when leaving the detail page, so the next one
         * opened never renders the previous project's name while its own fetch
         * is still in flight.
         */
        clearCurrentProject(state) {
            state.current = { data: null, isLoading: false, error: null };
        },
        clearMutationError(state) {
            state.mutation.error = null;
        },
        /**
         * Discard the hub and ACC project picker lists when the panel unmounts.
         * They are snapshots taken with one access token, and showing a stale
         * one against a refreshed token would offer picks the account may no
         * longer have. The connection itself is kept, because the projects list
         * reads it for the Autodesk column.
         */
        clearApsPickers(state, action: PayloadAction<string>) {
            const entry = state.aps[action.payload];
            if (!entry) return;
            entry.hubs = { data: [], isLoading: false, error: null };
            entry.accProjects = { hubId: null, data: [], isLoading: false, error: null };
            entry.action = { isBusy: false, error: null };
        },
        clearApsActionError(state, action: PayloadAction<string>) {
            const entry = state.aps[action.payload];
            if (entry) entry.action.error = null;
        },
        /**
         * The folder walk (`useFolderExpand`) drives its own pagination outside
         * any single thunk, and decides when a folder's subfolders are fully
         * discovered — a filtered page can come back empty before the real end
         * (see `fetchFolderContents`), so "loaded" cannot be inferred from any
         * one page's result and must be told to the store explicitly.
         */
        folderExpandComplete(state, action: PayloadAction<{ projectId: string; folderUrn: string }>) {
            const entry = apsEntry(state, action.payload.projectId);
            const node = entry.folders.byUrn[action.payload.folderUrn];
            if (!node) return;
            node.status = 'loaded';
            if (node.subfolders === null) node.subfolders = [];
            if (node.models === null) node.models = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProject.pending, (state) => {
                state.current.isLoading = true;
                state.current.error = null;
            })
            .addCase(fetchProject.fulfilled, (state, action: PayloadAction<Project>) => {
                state.current.isLoading = false;
                state.current.data = action.payload;
            })
            .addCase(fetchProject.rejected, (state, action) => {
                state.current.isLoading = false;
                state.current.data = null;
                state.current.error = action.payload ?? { message: 'Failed to load the project.' };
            })

            .addCase(createProject.fulfilled, (state, action: PayloadAction<Project>) => {
                state.mutation.isSaving = false;
                state.list.data = upsertIntoList(state.list.data, action.payload);
            })

            .addCase(renameProject.fulfilled, (state, action: PayloadAction<Project>) => {
                state.mutation.isSaving = false;
                state.list.data = upsertIntoList(state.list.data, action.payload);
                if (state.current.data?.object_id === action.payload.object_id) {
                    state.current.data = action.payload;
                }
            })

            .addCase(deleteProject.fulfilled, (state, action: PayloadAction<string>) => {
                state.mutation.isSaving = false;
                state.list.data = state.list.data.filter(p => p.object_id !== action.payload);
                if (state.current.data?.object_id === action.payload) {
                    state.current.data = null;
                }
                delete state.aps[action.payload];
            })

            // APS: connection read
            .addCase(fetchApsConnection.pending, (state, action) => {
                const entry = apsEntry(state, action.meta.arg.projectId);
                entry.status = 'loading';
                entry.error = null;
            })
            .addCase(fetchApsConnection.fulfilled, (state, action: PayloadAction<ApsConnectionResult>) => {
                const entry = apsEntry(state, action.payload.projectId);
                entry.status = 'loaded';
                entry.connection = action.payload.connection;
                entry.error = null;
            })
            .addCase(fetchApsConnection.rejected, (state, action) => {
                const projectId = action.payload?.projectId ?? action.meta.arg.projectId;
                const entry = apsEntry(state, projectId);
                entry.connection = null;
                // 404 is the answer "never connected", not a failure to report.
                if (action.payload?.status === 404) {
                    entry.status = 'notConnected';
                    entry.error = null;
                } else {
                    entry.status = 'error';
                    entry.error = action.payload ?? { message: 'Failed to read the Autodesk connection.' };
                }
            })

            // APS: hubs
            .addCase(fetchApsHubs.pending, (state, action) => {
                const entry = apsEntry(state, action.meta.arg.projectId);
                entry.hubs.isLoading = true;
                entry.hubs.error = null;
            })
            .addCase(fetchApsHubs.fulfilled, (state, action) => {
                const entry = apsEntry(state, action.payload.projectId);
                entry.hubs.isLoading = false;
                entry.hubs.data = action.payload.hubs;
            })
            .addCase(fetchApsHubs.rejected, (state, action) => {
                const entry = apsEntry(state, action.payload?.projectId ?? action.meta.arg.projectId);
                entry.hubs.isLoading = false;
                entry.hubs.data = [];
                entry.hubs.error = action.payload ?? { message: 'Failed to load Autodesk hubs.' };
            })

            // APS: ACC projects within a hub
            .addCase(fetchApsHubProjects.pending, (state, action) => {
                const entry = apsEntry(state, action.meta.arg.projectId);
                entry.accProjects = {
                    hubId: action.meta.arg.hubId,
                    data: [],
                    isLoading: true,
                    error: null,
                };
            })
            .addCase(fetchApsHubProjects.fulfilled, (state, action) => {
                const entry = apsEntry(state, action.payload.projectId);
                // Ignore a response for a hub the admin has already moved off.
                if (entry.accProjects.hubId !== action.payload.hubId) return;
                entry.accProjects.isLoading = false;
                entry.accProjects.data = action.payload.accProjects;
            })
            .addCase(fetchApsHubProjects.rejected, (state, action) => {
                const entry = apsEntry(state, action.payload?.projectId ?? action.meta.arg.projectId);
                if (entry.accProjects.hubId !== action.meta.arg.hubId) return;
                entry.accProjects.isLoading = false;
                entry.accProjects.error = action.payload ?? { message: 'Failed to load ACC projects.' };
            })

            // APS: link, which returns the updated connection
            .addCase(linkAccProject.fulfilled, (state, action: PayloadAction<ApsConnectionResult>) => {
                const entry = apsEntry(state, action.payload.projectId);
                entry.action.isBusy = false;
                entry.status = 'loaded';
                entry.connection = action.payload.connection;
                // A first link has nothing cached yet; a repoint (`allow_replace`)
                // means the catalog, groups and folder tree just fetched all
                // describe the *previous* ACC project and must not be shown
                // against this one.
                resetProjectCaches(entry);
            })

            .addCase(unlinkAccProject.fulfilled, (state, action) => {
                const entry = apsEntry(state, action.payload.projectId);
                entry.action.isBusy = false;
                if (entry.connection) {
                    // `unlinked` is the only state whose refs are null, so the
                    // three have to move together or a reader sees a state that
                    // still claims a pick.
                    entry.connection.acc_link = { state: 'unlinked', hub: null, project: null };
                }
                resetProjectCaches(entry);
            })

            .addCase(disconnectAps.fulfilled, (state, action) => {
                const entry = apsEntry(state, action.payload.projectId);
                entry.action.isBusy = false;
                // Disconnect revokes the credential but keeps the row, and the
                // hub/project pick survives so reconnecting does not ask again.
                if (entry.connection) entry.connection.status = 'revoked';
                entry.hubs = { data: [], isLoading: false, error: null };
                entry.accProjects = { hubId: null, data: [], isLoading: false, error: null };
            })

            .addCase(startApsConnect.fulfilled, (state, action) => {
                apsEntry(state, action.payload.projectId).action.isBusy = false;
            })

            // Folder tree: root
            .addCase(fetchTopFolders.pending, (state, action) => {
                const entry = apsEntry(state, action.meta.arg.projectId);
                entry.folders.top.isLoading = true;
                entry.folders.top.error = null;
            })
            .addCase(fetchTopFolders.fulfilled, (state, action) => {
                const entry = apsEntry(state, action.payload.projectId);
                entry.folders.top.isLoading = false;
                entry.folders.top.data = action.payload.folders;
                // Seeds each root's name so it's known before it is ever
                // expanded, same as any folder discovered as someone's child.
                for (const folder of action.payload.folders) {
                    const existing = entry.folders.byUrn[folder.id];
                    if (existing) existing.name = folder.name;
                    else entry.folders.byUrn[folder.id] = { name: folder.name, status: 'idle', subfolders: null, models: null, error: null };
                }
            })
            .addCase(fetchTopFolders.rejected, (state, action) => {
                const entry = apsEntry(state, action.payload?.projectId ?? action.meta.arg.projectId);
                entry.folders.top.isLoading = false;
                entry.folders.top.error = action.payload ?? { message: 'Failed to load the ACC folder tree.' };
            })

            // Folder tree: one page of one folder's children — both its
            // subfolders and its direct RVT files, from the same `?type=folder,rvt`
            // call, since the folder browser is the source of truth for both.
            // `useFolderExpand` dispatches this once per page and decides when
            // the walk is done (see `folderExpandComplete` above) — a fulfilled
            // page here never means completion by itself.
            .addCase(fetchFolderContents.pending, (state, action) => {
                const entry = apsEntry(state, action.meta.arg.projectId);
                const urn = action.meta.arg.folderUrn;
                const node = entry.folders.byUrn[urn] ?? { name: null, status: 'idle', subfolders: null, models: null, error: null };
                node.status = 'loading';
                node.error = null;
                entry.folders.byUrn[urn] = node;
            })
            .addCase(fetchFolderContents.fulfilled, (state, action) => {
                const entry = apsEntry(state, action.payload.projectId);
                const node = entry.folders.byUrn[action.payload.folderUrn];
                if (!node) return;
                if (node.subfolders === null) node.subfolders = [];
                if (node.models === null) node.models = [];
                const knownModelIds = new Set(node.models.map(m => m.id));
                for (const child of action.payload.entries) {
                    if (child.kind === 'folder') {
                        const existing = entry.folders.byUrn[child.id];
                        if (existing) existing.name = child.name;
                        else entry.folders.byUrn[child.id] = { name: child.name, status: 'idle', subfolders: null, models: null, error: null };
                        if (!node.subfolders.includes(child.id)) node.subfolders.push(child.id);
                    } else if (!knownModelIds.has(child.id)) {
                        knownModelIds.add(child.id);
                        node.models.push(child);
                    }
                }
            })
            .addCase(fetchFolderContents.rejected, (state, action) => {
                const projectId = action.payload?.projectId ?? action.meta.arg.projectId;
                const entry = apsEntry(state, projectId);
                const node = entry.folders.byUrn[action.meta.arg.folderUrn];
                if (!node) return;
                node.status = 'error';
                node.error = action.payload ?? { message: 'Failed to load this folder.' };
            })

            // The RVT catalog and its link graph
            .addCase(fetchRvtModels.pending, (state, action) => {
                const entry = apsEntry(state, action.meta.arg.projectId);
                entry.rvtGraph.status = 'loading';
                entry.rvtGraph.error = null;
            })
            .addCase(fetchRvtModels.fulfilled, (state, action) => {
                const entry = apsEntry(state, action.payload.projectId);
                entry.rvtGraph.status = 'loaded';
                entry.rvtGraph.computedAt = action.payload.graph.computed_at;
                entry.rvtGraph.nodes = action.payload.graph.nodes;
                entry.rvtGraph.edges = action.payload.graph.edges;
            })
            .addCase(fetchRvtModels.rejected, (state, action) => {
                const entry = apsEntry(state, action.payload?.projectId ?? action.meta.arg.projectId);
                entry.rvtGraph.status = 'error';
                entry.rvtGraph.error = action.payload ?? { message: 'Failed to load the model catalog.' };
            })

            // Model groups: read
            .addCase(fetchModelGroups.pending, (state, action) => {
                const entry = apsEntry(state, action.meta.arg.projectId);
                entry.modelGroups.status = 'loading';
                entry.modelGroups.error = null;
            })
            .addCase(fetchModelGroups.fulfilled, (state, action) => {
                const entry = apsEntry(state, action.payload.projectId);
                entry.modelGroups.status = 'loaded';
                entry.modelGroups.data = action.payload.groups;
            })
            .addCase(fetchModelGroups.rejected, (state, action) => {
                const entry = apsEntry(state, action.payload?.projectId ?? action.meta.arg.projectId);
                entry.modelGroups.status = 'error';
                entry.modelGroups.error = action.payload ?? { message: 'Failed to load model groups.' };
            })

            .addCase(deleteModelGroup.fulfilled, (state, action) => {
                const entry = apsEntry(state, action.payload.projectId);
                entry.modelGroups.mutation.isSaving = false;
                entry.modelGroups.data = entry.modelGroups.data.filter(g => g.object_id !== action.payload.groupId);
            })

            // Both list sources feed the same slot: a caller reads one or the
            // other depending on their role, never both, so a single `list` is
            // the whole answer for whoever is asking.
            .addMatcher(
                (action) => action.type === fetchProjects.pending.type || action.type === fetchMyProjects.pending.type,
                (state) => {
                    state.list.isLoading = true;
                    state.list.error = null;
                }
            )
            .addMatcher(
                (action): action is PayloadAction<Project[]> =>
                    action.type === fetchProjects.fulfilled.type || action.type === fetchMyProjects.fulfilled.type,
                (state, action) => {
                    state.list.isLoading = false;
                    state.list.data = [...action.payload].sort(byName);
                }
            )
            .addMatcher(
                (action): action is PayloadAction<ApiErrorPayload | undefined> =>
                    action.type === fetchProjects.rejected.type || action.type === fetchMyProjects.rejected.type,
                (state, action) => {
                    state.list.isLoading = false;
                    state.list.data = [];
                    state.list.error = action.payload ?? { message: 'Failed to load projects.' };
                }
            )

            // Every mutating thunk shares one saving flag and one error slot.
            .addMatcher(
                (action) => [createProject.pending.type, renameProject.pending.type, deleteProject.pending.type]
                    .includes(action.type),
                (state) => {
                    state.mutation.isSaving = true;
                    state.mutation.error = null;
                }
            )
            .addMatcher(
                (action): action is PayloadAction<ApiErrorPayload | undefined> =>
                    [createProject.rejected.type, renameProject.rejected.type, deleteProject.rejected.type]
                        .includes(action.type),
                (state, action) => {
                    state.mutation.isSaving = false;
                    state.mutation.error = action.payload ?? { message: 'The change could not be saved.' };
                }
            )

            // The four APS writes share one busy flag and one error slot.
            .addMatcher(
                (action: any) => [
                    startApsConnect.pending.type,
                    disconnectAps.pending.type,
                    linkAccProject.pending.type,
                    unlinkAccProject.pending.type,
                ].includes(action.type),
                (state, action: any) => {
                    const entry = apsEntry(state, action.meta.arg.projectId);
                    entry.action.isBusy = true;
                    entry.action.error = null;
                }
            )
            .addMatcher(
                (action: any) => [
                    startApsConnect.rejected.type,
                    disconnectAps.rejected.type,
                    linkAccProject.rejected.type,
                    unlinkAccProject.rejected.type,
                ].includes(action.type),
                (state, action: any) => {
                    const entry = apsEntry(state, action.payload?.projectId ?? action.meta.arg.projectId);
                    entry.action.isBusy = false;
                    entry.action.error = action.payload ?? { message: 'The Autodesk request failed.' };
                }
            )

            // Model group writes share one saving flag and one error slot, same
            // pattern as the connection actions above — the editor edits one
            // group at a time, so the three never overlap.
            .addMatcher(
                (action: any) => [
                    createModelGroup.pending.type,
                    replaceModelGroup.pending.type,
                    deleteModelGroup.pending.type,
                ].includes(action.type),
                (state, action: any) => {
                    const entry = apsEntry(state, action.meta.arg.projectId);
                    entry.modelGroups.mutation.isSaving = true;
                    entry.modelGroups.mutation.error = null;
                }
            )
            .addMatcher(
                (action: any) => [
                    createModelGroup.rejected.type,
                    replaceModelGroup.rejected.type,
                    deleteModelGroup.rejected.type,
                ].includes(action.type),
                (state, action: any) => {
                    const entry = apsEntry(state, action.payload?.projectId ?? action.meta.arg.projectId);
                    entry.modelGroups.mutation.isSaving = false;
                    entry.modelGroups.mutation.error = action.payload ?? { message: 'The model group could not be saved.' };
                }
            )
            // Create and replace both return the saved group; upsert either way.
            .addMatcher(
                (action): action is PayloadAction<{ projectId: string; group: ModelGroup }> =>
                    action.type === createModelGroup.fulfilled.type || action.type === replaceModelGroup.fulfilled.type,
                (state, action) => {
                    const entry = apsEntry(state, action.payload.projectId);
                    entry.modelGroups.mutation.isSaving = false;
                    const index = entry.modelGroups.data.findIndex(g => g.object_id === action.payload.group.object_id);
                    if (index === -1) entry.modelGroups.data.push(action.payload.group);
                    else entry.modelGroups.data[index] = action.payload.group;
                }
            )

            // A revoked credential is the connection's own state, not just the
            // error of whichever call happened to discover it, so the panel must
            // switch to "needs reconnect" rather than only flash a message.
            //
            // Deliberately spans every APS thunk, reads included. Any route
            // needing a live token can answer with this code, and the hub and
            // ACC project lists are usually the first to hit it: they are the
            // only calls that reach Autodesk, so on an idle project they fail
            // long before any write is attempted.
            .addMatcher(
                (action: any) =>
                    typeof action.type === 'string'
                    && action.type.startsWith('aps/')
                    && action.type.endsWith('/rejected')
                    && action.payload?.code === APS_CONNECTION_REVOKED,
                (state, action: any) => {
                    const entry = apsEntry(state, action.payload.projectId);
                    if (entry.connection) entry.connection.status = 'revoked';
                }
            );
    },
});

export const {
    clearCurrentProject,
    clearMutationError,
    clearApsPickers,
    clearApsActionError,
    folderExpandComplete,
} = projectsSlice.actions;
export const projectsReducer = projectsSlice.reducer;
